import React from 'react';
import { useEnquiryStore } from '../../store/useEnquiryStore';
import { BlindAwningSpecification } from '../../types';

export const SpecificationsForm: React.FC = () => {
  const { currentEnquiry, addSpecification, updateSpecification, removeSpecification } = useEnquiryStore();

  const handleSpecificationChange = (id: string, field: keyof BlindAwningSpecification, value: string | number) => {
    updateSpecification(id, { [field]: value });
  };

  const handleAddSpecification = () => {
    addSpecification();
  };

  const handleRemoveSpecification = (id: string) => {
    removeSpecification(id);
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-6">
        <h2 className="text-xl lg:text-2xl font-semibold text-gray-900 mb-2">Blind & Awning Specifications</h2>
        <p className="text-sm lg:text-base text-gray-600">Add detailed specifications for each blind or awning item (up to 20 items).</p>
      </div>

      <div className="space-y-6 lg:space-y-8">
        {currentEnquiry.specifications.map((spec, index) => (
          <div key={spec.id} className="bg-gray-50 p-4 lg:p-6 rounded-lg border border-gray-200">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-2">
              <h3 className="text-lg lg:text-xl font-medium text-gray-900">Item #{index + 1}</h3>
              <button
                onClick={() => handleRemoveSpecification(spec.id)}
                className="self-start sm:self-auto px-4 py-2 text-red-600 hover:text-red-800 text-sm font-medium bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
              >
                Remove Item
              </button>
            </div>

            {/* Mobile-Optimized Form Sections */}
            <div className="space-y-6">
              {/* Basic Info Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 lg:mb-4">Basic Information</h4>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
                      Item Number
                    </label>
                    <input
                      type="text"
                      value={spec.itemNumber}
                      onChange={(e) => handleSpecificationChange(spec.id, 'itemNumber', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., B001"
                    />
                  </div>

                  <div>
                    <label className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
                      Product
                    </label>
                    <select
                      value={spec.product}
                      onChange={(e) => handleSpecificationChange(spec.id, 'product', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">Select Product</option>
                      <option value="Roller Blind">Roller Blind</option>
                      <option value="Venetian Blind">Venetian Blind</option>
                      <option value="Vertical Blind">Vertical Blind</option>
                      <option value="Roman Blind">Roman Blind</option>
                      <option value="Panel Blind">Panel Blind</option>
                      <option value="Awning">Awning</option>
                      <option value="Outdoor Blind">Outdoor Blind</option>
                      <option value="Shutters">Shutters</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      value={spec.location}
                      onChange={(e) => handleSpecificationChange(spec.id, 'location', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Living Room, Kitchen"
                    />
                  </div>
                </div>
              </div>

              {/* Measurements Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 lg:mb-4">Measurements</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
                      Quantity
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={spec.quantity}
                      onChange={(e) => handleSpecificationChange(spec.id, 'quantity', parseInt(e.target.value) || 1)}
                      className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
                      Width (mm)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={spec.width}
                      onChange={(e) => handleSpecificationChange(spec.id, 'width', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Width in mm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
                      Drop (mm)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={spec.drop}
                      onChange={(e) => handleSpecificationChange(spec.id, 'drop', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Drop in mm"
                    />
                  </div>
                </div>
              </div>

              {/* Installation Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 lg:mb-4">Installation Details</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
                      Fit Type
                    </label>
                    <select
                      value={spec.fit}
                      onChange={(e) => handleSpecificationChange(spec.id, 'fit', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">Select Fit</option>
                      <option value="Inside">Inside</option>
                      <option value="Outside">Outside</option>
                      <option value="Exact">Exact</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
                      Fixing Method
                    </label>
                    <select
                      value={spec.fixing}
                      onChange={(e) => handleSpecificationChange(spec.id, 'fixing', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">Select Fixing</option>
                      <option value="Top Fix">Top Fix</option>
                      <option value="Face Fix">Face Fix</option>
                      <option value="Recess">Recess</option>
                      <option value="Ceiling">Ceiling</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Options Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 lg:mb-4">Options & Features</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
                      Material
                    </label>
                    <input
                      type="text"
                      value={spec.material}
                      onChange={(e) => handleSpecificationChange(spec.id, 'material', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Blockout, Sunscreen"
                    />
                  </div>

                  <div>
                    <label className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
                      Bottom Rail
                    </label>
                    <select
                      value={spec.bottomRail}
                      onChange={(e) => handleSpecificationChange(spec.id, 'bottomRail', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">Select Bottom Rail</option>
                      <option value="Standard">Standard</option>
                      <option value="Weighted">Weighted</option>
                      <option value="Concealed">Concealed</option>
                      <option value="None">None</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
                      Control Type
                    </label>
                    <select
                      value={spec.control}
                      onChange={(e) => handleSpecificationChange(spec.id, 'control', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">Select Control</option>
                      <option value="Chain">Chain</option>
                      <option value="Cord">Cord</option>
                      <option value="Motorized">Motorized</option>
                      <option value="Remote">Remote</option>
                      <option value="Wand">Wand</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
                      Stack Position
                    </label>
                    <select
                      value={spec.stack}
                      onChange={(e) => handleSpecificationChange(spec.id, 'stack', e.target.value)}
                      className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">Select Stack</option>
                      <option value="Left">Left</option>
                      <option value="Right">Right</option>
                      <option value="Center">Center</option>
                      <option value="Split">Split</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Comments Section */}
              <div>
                <label className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
                  Comments
                </label>
                <textarea
                  value={spec.comments}
                  onChange={(e) => handleSpecificationChange(spec.id, 'comments', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Additional comments or special requirements..."
                />
              </div>
            </div>
          </div>
        ))}

        <div className="text-center">
          <button
            onClick={handleAddSpecification}
            disabled={currentEnquiry.specifications.length >= 20}
            className="inline-flex items-center px-6 py-3 lg:px-4 lg:py-2 border border-transparent text-base lg:text-sm font-medium rounded-lg lg:rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span className="hidden sm:inline">Add Specification ({currentEnquiry.specifications.length}/20)</span>
            <span className="sm:hidden">Add Item ({currentEnquiry.specifications.length}/20)</span>
          </button>
        </div>

        {currentEnquiry.specifications.length === 0 && (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No specifications yet</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first blind or awning specification.</p>
          </div>
        )}
      </div>
    </div>
  );
};